import React, { Dispatch, SetStateAction } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import _get from 'lodash/get';
import { useStatus } from 'resift';
import * as Types from 'pages/Pages/typings';
import makeGetEntitiesListFetch from 'fetches/makeGetEntitiesListFetch';

import TableBody from '@material-ui/core/TableBody';
import Paper from '@material-ui/core/Paper';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import TablePagination from 'components/Table/TablePagination';
import TableSearchTextField from 'components/Table/TableSearchTextField';
import ExportButton from 'components/buttons/ExportButton';
import Toolbar from 'components/Toolbar';
import ToolbarSpacer from 'components/Toolbar/ToolbarSpacer';
import Loader from 'components/Loader';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import PagesRow from './PagesRow';

interface Props {
  isIndependentPage: boolean;
  pagesData: Types.EntitySearchResult;
  canAddOrDeletePages: boolean;
  onDelete: () => void;
  onSelectEntity: Dispatch<SetStateAction<Types.Entity | null>>;
  onSetCreateModalOpen: (status: boolean) => void;
}

function PagesTable({
  isIndependentPage,
  pagesData,
  canAddOrDeletePages,
  onDelete,
  onSelectEntity,
  onSetCreateModalOpen,
}: Props) {
  const classes = useStyles();
  const pages: Types.Entity[] = _get(pagesData, 'results', []);
  const defaultPagination = {
    total: 0,
    pageSize: 30,
  };
  const pagination = _get(pagesData, 'pagination', defaultPagination);
  const formats = _get(pagesData, 'formats');
  const makeGetEntitiesList = makeGetEntitiesListFetch();
  const entitiesListStatus = useStatus(makeGetEntitiesList);

  const NewPagesButton = (
    <Button
      disabled={!canAddOrDeletePages}
      onClick={() => onSetCreateModalOpen(true)}
      className={classes.marginLeft}
      variant={canAddOrDeletePages ? 'outlined' : 'contained'}
      color="secondary"
    >
      New Pages
    </Button>
  );

  return (
    <Paper className={classes.paper}>
      <Toolbar>
        <TableSearchTextField hintText="Search Pages" />
        <ToolbarSpacer />
        <ExportButton formats={formats} />
        {canAddOrDeletePages ? (
          NewPagesButton
        ) : (
          <Tooltip
            arrow={true}
            placement="top"
            title="Your pages are being automatically managed via data sync"
          >
            <div>{NewPagesButton}</div>
          </Tooltip>
        )}
      </Toolbar>
      <Loader status={entitiesListStatus} style={{ display: 'contents' }}>
        <Table>
          <TableHead>
            <TableHeadCell sortByKey="name"> Name </TableHeadCell>
            <TableHeadCell sortByKey="editors">
              <>
                Assigned Editors
                <Tooltip title="The number of editors who have been explicitly assigned to this page. Does not count editors who have access through the defined editor access rule in “General Settings”">
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
              </>
            </TableHeadCell>
            {!isIndependentPage && (
              <TableHeadCell sortByKey="members"> Member Count </TableHeadCell>
            )}
            <TableHeadCell sortByKey="lastEditedAt"> Last Updated At </TableHeadCell>
            <TableHeadCell />
          </TableHead>
          <TableBody>
            {pages.map((page: Types.Entity, index: number) => (
              <PagesRow
                deletable={canAddOrDeletePages}
                key={`page-${index}`}
                classes={classes}
                page={page}
                isIndependentPage={isIndependentPage}
                onDelete={onDelete}
                handleSelectEntity={onSelectEntity}
              />
            ))}
          </TableBody>
        </Table>
        <TablePagination data-testid="pages-pagination" pagination={pagination} component="div" />
      </Loader>
    </Paper>
  );
}

const useStyles = makeStyles((theme: any) => ({
  paper: {
    marginTop: theme.spacing(3),
    width: '100%',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  textAlignRight: {
    textAlign: 'right',
  },
  infoIcon: {
    marginLeft: theme.spacing(),
    color: theme.colors.gray1,
  },
  marginLeft: {
    marginLeft: theme.spacing(3),
  },
  pageLink: {
    color: 'inherit',
  },
}));

export default PagesTable;
